/**
 * BracketTab component
 *
 * Displays the Commander bracket evaluation for the current deck.
 * Uses the useBracket hook for state management.
 *
 * Sections:
 * - BracketBadge: color-coded bracket label
 * - GameChangersSection: list of game changer cards in the deck
 * - CombosSection: infinite combos detected by Commander Spellbook
 *
 * Card names are hoverable to update the card preview area.
 */

import type { BracketLabel, Card } from '@mtg-deck-tool/shared';
import { useBracket } from '../../hooks/useBracket.js';
import { useCardPreview } from '../../contexts/CardPreviewContext.js';
import { useDeckStore } from '../../stores/deck-store.js';

// ─── Helper ───────────────────────────────────────────────────────────────────

function bracketDisplayLabel(bracket: BracketLabel): string {
  switch (bracket) {
    case '1/2':
      return 'Bracket 1/2 — Casual';
    case '3':
      return 'Bracket 3 — Upgraded';
    case '4/5':
      return 'Bracket 4/5 — High Power';
    default:
      return `Bracket ${bracket as string}`;
  }
}

function bracketColorClasses(bracket: BracketLabel): string {
  switch (bracket) {
    case '1/2':
      return 'text-green-400 border-green-600';
    case '3':
      return 'text-amber-400 border-amber-600';
    case '4/5':
      return 'text-red-400 border-red-600';
    default:
      return 'text-gray-400 border-gray-600';
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface BracketBadgeProps {
  bracket: BracketLabel;
}

function BracketBadge({ bracket }: BracketBadgeProps) {
  const colorClasses = bracketColorClasses(bracket);
  return (
    <div className={`border rounded-lg p-4 mb-4 ${colorClasses}`}>
      <p className={`text-2xl font-bold ${colorClasses}`}>
        {bracketDisplayLabel(bracket)}
      </p>
    </div>
  );
}

interface HoverableCardNameProps {
  name: string;
  lookupCard: (name: string) => Card | undefined;
  setHoveredCard: (card: Card | null) => void;
}

function HoverableCardName({ name, lookupCard, setHoveredCard }: HoverableCardNameProps) {
  const handleMouseEnter = () => {
    const card = lookupCard(name);
    if (card) setHoveredCard(card);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  return (
    <button
      className="text-sm text-gray-300 hover:text-orange-400 transition-colors text-left"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {name}
    </button>
  );
}

interface GameChangersSectionProps {
  gameChangers: string[];
  lookupCard: (name: string) => Card | undefined;
  setHoveredCard: (card: Card | null) => void;
}

function GameChangersSection({ gameChangers, lookupCard, setHoveredCard }: GameChangersSectionProps) {
  const count = gameChangers.length;
  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {count} Game Changer{count !== 1 ? 's' : ''}
      </h3>
      {count === 0 ? (
        <p className="text-sm text-gray-600">No game changers detected.</p>
      ) : (
        <ul className="space-y-1">
          {gameChangers.map((name) => (
            <li key={name}>
              <HoverableCardName
                name={name}
                lookupCard={lookupCard}
                setHoveredCard={setHoveredCard}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CombosSectionProps {
  combos: ReturnType<typeof useBracket>['combos'];
  isLoading: boolean;
  comboDetectionAvailable: boolean;
  lookupCard: (name: string) => Card | undefined;
  setHoveredCard: (card: Card | null) => void;
}

function CombosSection({
  combos,
  isLoading,
  comboDetectionAvailable,
  lookupCard,
  setHoveredCard,
}: CombosSectionProps) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        Infinite Combos
      </h3>
      {isLoading ? (
        <p className="text-sm text-gray-500">Checking for infinite combos...</p>
      ) : !comboDetectionAvailable ? (
        <p className="text-sm text-gray-600">Combo detection unavailable.</p>
      ) : combos.length === 0 ? (
        <p className="text-sm text-gray-600">No infinite combos detected.</p>
      ) : (
        <ul className="space-y-3">
          {combos.map((combo) => (
            <li key={combo.id} className="text-sm">
              <div className="flex flex-wrap gap-1 items-center">
                {combo.cardNames.map((name: string, i: number) => (
                  <span key={name} className="inline-flex items-center">
                    <HoverableCardName
                      name={name}
                      lookupCard={lookupCard}
                      setHoveredCard={setHoveredCard}
                    />
                    {i < combo.cardNames.length - 1 && (
                      <span className="text-gray-600 mx-1">,</span>
                    )}
                  </span>
                ))}
              </div>
              <p className="text-gray-500 mt-1">
                → {combo.results.join(', ') || 'Combo'}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Card list section (reusable for extra turns / MLD) ──────────────────────

interface CardListSectionProps {
  title: string;
  cards: string[];
  emptyMessage: string;
  lookupCard: (name: string) => Card | undefined;
  setHoveredCard: (card: Card | null) => void;
  badgeColor?: string;
}

function CardListSection({ title, cards, emptyMessage, lookupCard, setHoveredCard, badgeColor }: CardListSectionProps) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {cards.length > 0 && badgeColor && (
          <span className={`inline-block w-2 h-2 rounded-full ${badgeColor} mr-1.5`} />
        )}
        {cards.length} {title}
      </h3>
      {cards.length === 0 ? (
        <p className="text-sm text-gray-600">{emptyMessage}</p>
      ) : (
        <ul className="space-y-1">
          {cards.map((name) => (
            <li key={name}>
              <HoverableCardName
                name={name}
                lookupCard={lookupCard}
                setHoveredCard={setHoveredCard}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BracketTab() {
  const { bracket, gameChangers, combos, isLoading, comboDetectionAvailable, extraTurnCards, massLandDenialCards } = useBracket();
  const { setHoveredCard } = useCardPreview();
  const entries = useDeckStore((s) => s.entries);

  const lookupCard = (name: string): Card | undefined => {
    const lower = name.toLowerCase();
    return entries.find((e) => e.card.name.toLowerCase() === lower)?.card;
  };

  if (bracket === null) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6 py-8">
        <p className="text-gray-600 text-sm">Import a deck to evaluate its bracket.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <BracketBadge bracket={bracket} />
      <GameChangersSection
        gameChangers={gameChangers}
        lookupCard={lookupCard}
        setHoveredCard={setHoveredCard}
      />
      {massLandDenialCards.length > 0 && (
        <CardListSection
          title="Mass Land Denial"
          cards={massLandDenialCards}
          emptyMessage=""
          lookupCard={lookupCard}
          setHoveredCard={setHoveredCard}
          badgeColor="bg-red-500"
        />
      )}
      <CardListSection
        title={`Extra Turn Spell${extraTurnCards.length !== 1 ? 's' : ''}`}
        cards={extraTurnCards}
        emptyMessage="No extra turn spells detected."
        lookupCard={lookupCard}
        setHoveredCard={setHoveredCard}
        badgeColor="bg-purple-500"
      />
      <CombosSection
        combos={combos}
        isLoading={isLoading}
        comboDetectionAvailable={comboDetectionAvailable}
        lookupCard={lookupCard}
        setHoveredCard={setHoveredCard}
      />
    </div>
  );
}
